import React, { useState, useEffect } from 'react';
import {
  Card,
  Rate,
  Empty,
  Spin,
  Pagination,
  Select,
  Input,
  Tag,
  Avatar,
  Divider,
  Statistic,
  Row,
  Col,
  Typography
} from 'antd';
import {
  UserOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  StarOutlined
} from '@ant-design/icons';
import inspectionService from '@/services/inspection.service';

const { Search } = Input;
const { Option } = Select;

const FeedbackList = ({ onRefresh }) => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [filters, setFilters] = useState({
    event: null,
    user_name: null
  });
  const [stats, setStats] = useState([]);



  const fetchFeedbacks = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString(),
        ...(filters.event && { event: filters.event }),
        ...(filters.user_name && { user_name: filters.user_name })
      });

      const response = await inspectionService.getAllFeeback();

      setFeedbacks(response?.data.ratings);
      setPagination({
        current: response.data.currentPage,
        pageSize: pageSize,
        total: response.data.totalCount
      });

    } catch (error) {
      console.error('Error fetching feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await inspectionService.getAllFeebackStat();
      setStats(response.data);

    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, [filters]);

  const handlePageChange = (page, pageSize) => {
    fetchFeedbacks(page, pageSize);
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEventColor = (event) => {
    const colors = {
      'Product Experience': 'blue',
      'Customer Service': 'green',
      'Website Usability': 'purple',
      'Inspection Process': 'orange',
      'Report Quality': 'cyan',
      'Support Response': 'magenta',
      'Other': 'default'
    };
    return colors[event] || 'default';
  };

  return (
    <div className="max-w-8xl mx-auto p-6 bg-gray-50 min-h-screen">
      {/* Header with Stats */}
      <div className="mb-8">
        <Typography.Title level={5} className="  text-gray-800 mb-6">Feedbacks</Typography.Title>

        {stats.length > 0 && (
          <Row gutter={16} className="mb-6">
            {stats.slice(0, 4).map((stat, index) => (
              <Col span={6} key={index}>
                <Card size='small' className="text-center shadow-sm hover:shadow-md transition-shadow">
                  <Statistic
                    title={stat.event}
                    value={parseFloat(stat.averageRating).toFixed(1)}
                    prefix={<StarOutlined className="text-yellow-500" />}
                    suffix={`(${stat.totalRatings})`}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </div>



      {/* Feedback List */}
      <Spin spinning={loading}>
        {feedbacks?.length === 0 ? (
          <Card className="text-center py-12">
            <Empty
              description="No feedback found"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        ) : (
          <div className="space-y-4">
            {feedbacks?.map((feedback) => (
              <div
                key={feedback.id}
                className="shadow-sm hover:shadow-md transition-shadow border-l-4 p-2 rounded-xl border-l-green-500"
              >
                <div className="flex justify-between items-start">
                  {/* Left side - User and Rating */}
                  <div className="flex items-start gap-4 flex-1">
                    <Avatar
                      icon={<UserOutlined />}
                      className="bg-green-500"
                    />

                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-800">
                          {feedback.user_name}
                        </span>
                        <Tag color={getEventColor(feedback.event)}>
                          {feedback.event}
                        </Tag>

                        <div className="flex items-center gap-3 mb-3">
                          <Rate
                            disabled
                            value={feedback.rating}
                            className="text-sm"
                          />
                          <span className={`font-bold ${getRatingColor(feedback.rating)}`}>
                            {feedback.rating}/5
                          </span>
                        </div>
                      </div>

                      {feedback.comments && (
                        <div className="bg-gray-50 p-3 rounded-md">
                          <MessageOutlined className="text-gray-500 mr-2" />
                          <span className="text-gray-700">{feedback.comments}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side - Date */}
                  <div className="text-right text-gray-500 text-sm">
                    <div className="flex items-center gap-1">
                      <ClockCircleOutlined />
                      {new Date(feedback.dateTime).toLocaleDateString()}
                    </div>
                    <div className="text-xs mt-1">
                      {new Date(feedback.dateTime).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Spin>
       {/* Pagination */}
      {feedbacks?.length > 0 && (
        <div className="mt-8 text-center">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            onChange={handlePageChange}
            onShowSizeChange={handlePageChange}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} feedbacks`
            }
            className="bg-white p-4 rounded-lg shadow-sm"
          />
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
